import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import { companyInfo } from '../../utils/dataInfo';

// Helper function to get customer name
const getCustomerName = (customerId, customerData) => {
  if (!customerData || !Array.isArray(customerData)) return 'Unknown';
  const customer = customerData.find(c => parseInt(c.id) === parseInt(customerId));
  return customer ? customer.customer_name : 'Unknown';
};

// Helper function to get branch name
const getBranchName = (branchId, branchData) => {
  if (!branchData || !Array.isArray(branchData)) return 'Unknown';
  const branch = branchData.find(b => parseInt(b.id) === parseInt(branchId));
  return branch ? `${branch.name}` : 'Unknown';
};

// Helper function to get payment method name
const getPaymentMethodName = (method) => {
  const methods = {
    'cash': 'Cash',
    'card': 'Card',
    'bank': 'Bank',
    'mobile': 'Mobile'
  };
  return methods[method] || method;
};

// Helper function to get status name
const getStatusName = (status) => {
  return status === 1 ? 'Active' : 'Inactive';
};

// Helper function to format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US').format(amount) + " "+"sh";
};

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

// Manual table drawing function for PDF
// Manual table drawing function for PDF with NUMBERS column
const drawHiringVenuTable = (doc, data, startY) => {
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const margin = 20;
  const rowHeight = 12;
  
  // Column widths for 10 columns (added # column)
  const colWidths = [15, 35, 25, 40, 25, 25, 30, 30, 30, 30]; // #, Number, Date, Customer, Total, Paid, Balance, Payment, Status, Branch
  
  // Check if we need a new page
  let currentY = startY;
  const neededHeight = (data.length + 1) * rowHeight;
  
  if (currentY + neededHeight > pageHeight - 20) {
    doc.addPage('landscape');
    currentY = margin;
    
    // Redraw header on new page
    doc.setFillColor(255, 255, 255);
    doc.setTextColor(25, 118, 210);
    doc.setFont(undefined, 'bold');
    
    // Draw header background
    doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
    
    const headers = ['#', 'Number', 'Date', 'Customer', 'Total', 'Paid', 'Balance', 'Payment', 'Status', 'Branch'];
    let xPos = margin;
    
    headers.forEach((header, index) => {
      doc.text(header, xPos + 2, currentY + 8);
      xPos += colWidths[index];
    });
    
    // Draw header underline
    doc.setDrawColor(25, 118, 210);
    doc.setLineWidth(0.5);
    doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);
    
    currentY += rowHeight;
    doc.setFont(undefined, 'normal');
    doc.setTextColor(0, 0, 0);
  }
  
  // Draw table header if not on new page
  if (startY === currentY) {
    doc.setFillColor(25, 118, 210);
    doc.setTextColor(255, 255, 255);
    doc.setFont(undefined, 'bold');
    
    // Draw header background
    doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
    
    const headers = ['#', 'Number', 'Date', 'Customer', 'Total', 'Paid', 'Balance', 'Payment', 'Status', 'Branch'];
    let xPos = margin;
    
    headers.forEach((header, index) => {
      doc.text(header, xPos + 2, currentY + 8);
      xPos += colWidths[index];
    });
    
    // Draw header underline
    doc.setDrawColor(255, 255, 255);
    doc.setLineWidth(0.5);
    doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);
    
    currentY += rowHeight;
    doc.setFont(undefined, 'normal');
    doc.setTextColor(0, 0, 0);
  }
  
  // Draw table rows
  data.forEach((row, rowIndex) => {
    // Check for page break
    if (currentY + rowHeight > pageHeight - 20) {
      doc.addPage('landscape');
      currentY = margin;
      
      // Redraw header on new page
      doc.setFillColor(25, 118, 210);
      doc.setTextColor(255, 255, 255);
      doc.setFont(undefined, 'bold');
      
      // Draw header background
      doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
      
      const headers = ['#', 'Number', 'Date', 'Customer', 'Total', 'Paid', 'Balance', 'Payment', 'Status', 'Branch'];
      let xPos = margin;
      
      headers.forEach((header, index) => {
        doc.text(header, xPos + 2, currentY + 8);
        xPos += colWidths[index];
      });
      
      // Draw header underline
      doc.setDrawColor(255, 255, 255);
      doc.setLineWidth(0.5);
      doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);
      
      currentY += rowHeight;
      doc.setFont(undefined, 'normal');
      doc.setTextColor(0, 0, 0);
    }
    
    // Alternate row colors
    if (rowIndex % 2 === 0) {
      doc.setFillColor(245, 245, 245);
    } else {
      doc.setFillColor(255, 255, 255);
    }
    
    // Draw row background
    doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
    
    // Draw row borders
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.2);
    doc.line(margin, currentY, pageWidth - margin, currentY);
    
    // Draw row text
    let xPos = margin;
    row.forEach((cell, cellIndex) => {
      // Truncate text if too long
      let text = String(cell);
      if (cellIndex === 3 && text.length > 15) { // Customer name (now at index 3)
        text = text.substring(0, 12) + '...';
      } else if (text.length > 20) {
        text = text.substring(0, 17) + '...';
      }
      
      // Center align the number column
      if (cellIndex === 0) {
        const textWidth = doc.getTextWidth(text);
        const centerX = xPos + (colWidths[cellIndex] / 2) - (textWidth / 2);
        doc.text(text, centerX, currentY + 8);
      } else {
        doc.text(text, xPos + 2, currentY + 8);
      }
      xPos += colWidths[cellIndex];
    });
    
    currentY += rowHeight;
  });
  
  return currentY;
};

// PDF export for Hiring Venu - Shows only specified columns in landscape WITH NUMBERS
export const exportToPDF = (hiringVenus, branchData = [], customerData = [], filters = {}) => {
  try {
    // Create PDF in landscape mode
    const doc = new jsPDF('landscape');
    
    // Set document properties
    doc.setProperties({
      title: 'Hiring Venu Report',
      subject: 'Hiring Venu Export',
      author: companyInfo.name,
      keywords: 'hiring, venu, export, report',
      creator: companyInfo.name
    });
    
    // Title section
    doc.setFontSize(20);
    doc.setTextColor(25, 118, 210);
    doc.text('HIRING VENU REPORT', doc.internal.pageSize.width / 2, 20, { align: 'center' });
    
    // Company/System name
    doc.setFontSize(12);
    doc.setTextColor(100, 100, 100);
    doc.text(companyInfo.name, doc.internal.pageSize.width / 2, 30, { align: 'center' });
    
    // Generation details
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    let currentY = 40;
    
    // Report info in two columns
    const leftColumnX = 20;
    const rightColumnX = doc.internal.pageSize.width / 2 + 20;
    
    doc.text(`Report Generated: ${new Date().toLocaleString()}`, leftColumnX, currentY);
    doc.text(`Total Records: ${hiringVenus.length}`, rightColumnX, currentY);
    currentY += 8;
    
    // Calculate totals
    if (hiringVenus.length > 0) {
      const totalAmount = hiringVenus.reduce((sum, hiring) => sum + parseFloat(hiring.total_amount || 0), 0);
      const totalPaid = hiringVenus.reduce((sum, hiring) => sum + parseFloat(hiring.paid_amount || 0), 0);
      const totalBalance = hiringVenus.reduce((sum, hiring) => sum + parseFloat(hiring.balance_amount || 0), 0);
      
      doc.text(`Total Amount: ${formatCurrency(totalAmount)}`, leftColumnX, currentY);
      doc.text(`Total Paid: ${formatCurrency(totalPaid)}`, rightColumnX, currentY);
      currentY += 8;
      doc.text(`Total Balance: ${formatCurrency(totalBalance)}`, leftColumnX, currentY);
      currentY += 8;
    }
    
    // Filters applied
    if (filters.customer_id || filters.payment_method || filters.dateFrom || filters.dateTo || filters.status || filters.branch_id) {
      doc.setFontSize(9);
      doc.setTextColor(60, 60, 60);
      doc.text('Applied Filters:', leftColumnX, currentY);
      currentY += 6;
      
      let filterLines = [];
      if (filters.customer_id) {
        const customer = customerData.find(c => c.id === filters.customer_id);
        filterLines.push(`• Customer: ${customer ? customer.customer_name : 'Unknown'}`);
      }
      if (filters.payment_method) {
        filterLines.push(`• Payment: ${getPaymentMethodName(filters.payment_method)}`);
      }
      if (filters.branch_id) {
        const branch = branchData.find(b => b.id === filters.branch_id);
        filterLines.push(`• Branch: ${branch ? branch.name : 'Unknown'}`);
      }
      if (filters.status) filterLines.push(`• Status: ${getStatusName(filters.status)}`);
      if (filters.dateFrom) filterLines.push(`• From: ${new Date(filters.dateFrom).toLocaleDateString()}`);
      if (filters.dateTo) filterLines.push(`• To: ${new Date(filters.dateTo).toLocaleDateString()}`);
      
      // Display filters in two columns if many
      const midPoint = Math.ceil(filterLines.length / 2);
      const leftFilters = filterLines.slice(0, midPoint);
      const rightFilters = filterLines.slice(midPoint);
      
      const maxLines = Math.max(leftFilters.length, rightFilters.length);
      
      for (let i = 0; i < maxLines; i++) {
        if (leftFilters[i]) {
          doc.text(leftFilters[i], leftColumnX + 5, currentY);
        }
        if (rightFilters[i]) {
          doc.text(rightFilters[i], rightColumnX, currentY);
        }
        currentY += 5;
      }
      currentY += 5;
    }
    
    // Prepare table data with specified columns INCLUDING NUMBERS
    const tableData = hiringVenus.map((hiring, index) => [
      (index + 1).toString(), // ADDED: Number column
      hiring.hiring_no || '',
      formatDate(hiring.created_at),
      getCustomerName(hiring.customer_id, customerData),
      formatCurrency(hiring.total_amount || 0),
      formatCurrency(hiring.paid_amount || 0),
      formatCurrency(hiring.balance_amount || 0),
      getPaymentMethodName(hiring.payment_method),
      getStatusName(hiring.status),
      getBranchName(hiring.branch_id, branchData)
    ]);
    
    // Draw the table (updated to handle 10 columns now)
    currentY = drawHiringVenuTable(doc, tableData, currentY + 10);
    
    // Add footer with page number
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text(
        `Page ${i} of ${pageCount}`,
        doc.internal.pageSize.width / 2,
        doc.internal.pageSize.height - 10,
        { align: 'center' }
      );
      doc.text(
        `Generated on: ${new Date().toLocaleDateString()}`,
        doc.internal.pageSize.width - 20,
        doc.internal.pageSize.height - 10,
        { align: 'right' }
      );
    }
    
    const fileName = `hiring_venu_report_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);

    return { success: true, fileName };
  } catch (error) {
    console.error('Hiring Venu PDF export failed:', error);
    throw new Error('Failed to export to PDF: ' + error.message);
  }
};
// Excel export for Hiring Venu - Shows all attributes
export const exportToExcel = (hiringVenus, branchData = [], customerData = [], filters = {}) => {
  try {
    // Prepare data for Excel with all attributes
    const excelData = hiringVenus.map((hiring, index) => ({
      'No.': index + 1,
      'Hiring ID': hiring.id,
      'Hiring Number': hiring.hiring_no,
      'Customer ID': hiring.customer_id,
      'Customer Name': getCustomerName(hiring.customer_id, customerData),
      'Customer Phone': hiring.customer_phone || '',
      'Customer Email': hiring.customer_email || '',
      'Customer Address': hiring.customer_address || '',
      'Additional Info': hiring.additional_info || '',
      'Total Amount': parseFloat(hiring.total_amount || 0),
      'Paid Amount': parseFloat(hiring.paid_amount || 0),
      'Balance Amount': parseFloat(hiring.balance_amount || 0),
      'Payment Method': hiring.payment_method,
      'Formatted Payment Method': getPaymentMethodName(hiring.payment_method),
      'Status ID': hiring.status,
      'Status': getStatusName(hiring.status),
      'Branch ID': hiring.branch_id,
      'Branch': getBranchName(hiring.branch_id, branchData),
      'Created Date': new Date(hiring.created_at).toLocaleDateString('en-GB'),
      'Created Time': new Date(hiring.created_at).toLocaleTimeString('en-GB'),
      'Updated Date': new Date(hiring.updated_at).toLocaleDateString('en-GB'),
      'Updated Time': new Date(hiring.updated_at).toLocaleTimeString('en-GB'),
      'Created At Full': new Date(hiring.created_at).toLocaleString('en-GB'),
      'Updated At Full': new Date(hiring.updated_at).toLocaleString('en-GB')
    }));

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Auto-size columns
    const maxWidth = excelData.reduce((widths, row) => {
      Object.keys(row).forEach((key, idx) => {
        const length = String(row[key]).length;
        if (!widths[idx] || length > widths[idx]) {
          widths[idx] = length;
        }
      });
      return widths;
    }, []);
    
    worksheet['!cols'] = maxWidth.map(width => ({ width: Math.min(width + 2, 50) }));

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Hiring Venu');

    // Generate Excel file and download
    const fileName = `hiring_venu_export_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    return { success: true, fileName };
  } catch (error) {
    console.error('Hiring Venu Excel export failed:', error);
    throw new Error('Failed to export to Excel: ' + error.message);
  }
};

// CSV export for Hiring Venu - Shows all attributes
export const exportToCSV = (hiringVenus, branchData = [], customerData = []) => {
  try {
    const headers = [
      'Hiring ID',
      'Hiring Number',
      'Customer ID',
      'Customer Name',
      'Customer Phone',
      'Customer Email',
      'Customer Address',
      'Additional Info',
      'Total Amount',
      'Paid Amount',
      'Balance Amount',
      'Payment Method',
      'Formatted Payment Method',
      'Status ID',
      'Status',
      'Branch ID',
      'Branch',
      'Created Date',
      'Created Time',
      'Updated Date',
      'Updated Time',
      'Created At Full',
      'Updated At Full'
    ];
    
    const csvData = hiringVenus.map(hiring => [
      hiring.id,
      hiring.hiring_no,
      hiring.customer_id,
      `"${getCustomerName(hiring.customer_id, customerData).replace(/"/g, '""')}"`,
      hiring.customer_phone || '',
      hiring.customer_email || '',
      `"${(hiring.customer_address || '').replace(/"/g, '""')}"`,
      `"${(hiring.additional_info || '').replace(/"/g, '""')}"`,
      parseFloat(hiring.total_amount || 0).toFixed(2),
      parseFloat(hiring.paid_amount || 0).toFixed(2),
      parseFloat(hiring.balance_amount || 0).toFixed(2),
      hiring.payment_method,
      getPaymentMethodName(hiring.payment_method),
      hiring.status,
      getStatusName(hiring.status),
      hiring.branch_id,
      `"${getBranchName(hiring.branch_id, branchData).replace(/"/g, '""')}"`,
      `"${new Date(hiring.created_at).toLocaleDateString('en-GB')}"`,
      `"${new Date(hiring.created_at).toLocaleTimeString('en-GB')}"`,
      `"${new Date(hiring.updated_at).toLocaleDateString('en-GB')}"`,
      `"${new Date(hiring.updated_at).toLocaleTimeString('en-GB')}"`,
      `"${new Date(hiring.created_at).toLocaleString('en-GB')}"`,
      `"${new Date(hiring.updated_at).toLocaleString('en-GB')}"`
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `hiring_venu_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { success: true };
  } catch (error) {
    console.error('Hiring Venu CSV export failed:', error);
    throw new Error('Failed to export to CSV: ' + error.message);
  }
};